import store from '@/store'
import AuthApi from './api/authApi'
import env from '@/env'

// Create a factory function that can be mocked in tests
export const createAuthApi = () => new AuthApi()

const ACCESS_TOKEN_STORAGE_KEY = 'ACCESS_TOKEN'
const MIGRATION_COMPLETED_KEY = 'TOKEN_MIGRATION_COMPLETED'

const sessionService = {
  // Keep a reference to the authApi instance
  _authApi: null,

  // Getter for authApi that creates it if needed
  get authApi() {
    if (!this._authApi) {
      this._authApi = createAuthApi()
    }
    return this._authApi
  },

  // Check if localStorage should be used for access token
  useLocalStorageForToken() {
    return env.useLocalStorageForToken()
  },

  getUserInfo() {
    try {
      let user = JSON.parse(localStorage.getItem('USER_INFO'))
      return user
    } catch (e) {
      console.error(e)
    }
  },

  getRole() {
    const user = this.getUserInfo()
    return user ? user.role : 'VISITOR'
  },

  getUserId() {
    let user = this.getUserInfo()
    return user.id
  },

  setUserInfo(user) {
    localStorage.setItem('USER_INFO', JSON.stringify(user))
  },

  async migrateTokenIfNeeded() {
    console.log('migrateTokenIfNeeded called')
    // Skip if migration already completed
    if (localStorage.getItem(MIGRATION_COMPLETED_KEY) === 'true') {
      return
    }

    // Skip if we're configured to use localStorage for tokens (local development mode)
    if (this.useLocalStorageForToken()) {
      console.log('Using localStorage for tokens, skipping migration')
      return
    }

    // Check if token is missing from store but exists in localStorage
    const storeToken = store?.state?.auth?.accessToken
    const localStorageToken = localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY)

    if (!storeToken && localStorageToken) {
      try {
        // Validate the token from localStorage
        const isValid = !this.isTokenExpiredFromToken(localStorageToken)

        if (isValid) {
          // Copy token to store
          store.commit('auth/setAccessToken', localStorageToken)
          console.log('Migrated access token from localStorage to store')

          // Get user info and restore it to the store if it exists
          const userInfo = this.getUserInfo()
          if (userInfo) {
            store.commit('setUser', userInfo)
            console.log('Restored user info to store during migration')
          }

          // Call API to set refresh token in cookie
          try {
            await this.authApi.setRefreshTokenInCookieForMigration()

            // Mark migration as completed
            localStorage.setItem(MIGRATION_COMPLETED_KEY, 'true')
            console.log('Successfully set refresh token in cookie for migration')
          } catch (error) {
            console.error('Failed to set refresh token in cookie:', error)
          }
        } else {
          console.log('Found expired token in localStorage, not migrating')
        }
      } catch (e) {
        console.error('Error during token migration:', e)
      }
    } else {
      console.log('No accessToken in memory or in localStorage, no migration needed')
    }
  },

  getAccessToken() {
    try {
      // First try to get token from store (new method)
      if (store?.state?.auth?.accessToken) {
        return store.state.auth.accessToken
      }

      // Check if we should use localStorage for token storage (local development mode)
      if (this.useLocalStorageForToken()) {
        return localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY)
      }

      // If we reach here, token is not found
      return null
    } catch (e) {
      console.error('Error getting access token:', e)
      return null
    }
  },

  setAccessToken(token) {
    try {
      // Always set token in store (new method)
      if (store && typeof store.commit === 'function') {
        store.commit('auth/setAccessToken', token)
      }

      // Check if we should use localStorage for token storage (local development mode)
      if (this.useLocalStorageForToken()) {
        console.warn('Using localStorage for access token. This is not secure for production.')
        if (token) {
          localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, token)
        } else {
          localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY)
        }
      }
    } catch (e) {
      console.error('Error setting access token:', e)
    }
  },

  destroy() {
    try {
      // Clear token from all possible storage locations
      if (this.useLocalStorageForToken()) {
        localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY)
      }

      if (store && typeof store.commit === 'function') {
        store.commit('auth/setAccessToken', null)
      }

      // Always clear user info
      localStorage.removeItem('USER_INFO')
    } catch (e) {
      console.error('Error during session destroy:', e)
      // Ensure USER_INFO is removed even if store commit fails
      localStorage.removeItem('USER_INFO')
    }
  },

  async refreshAccessToken() {
    try {
      const response = await this.authApi.refreshToken()
      if (response && response.data && response.data.access_token) {
        this.setAccessToken(response.data.access_token)
        return response.data.access_token
      }
      return null
    } catch (e) {
      console.error('Failed to refresh token:', e)
      // If refresh fails, logout the user
      this.destroy()
      return null
    }
  },

  isTokenExpiredFromToken(token) {
    if (!token) return true

    try {
      // JWT tokens are in three parts: header.payload.signature
      const payload = token.split('.')[1]
      const decodedPayload = JSON.parse(atob(payload))

      // Check if token is expired
      const currentTime = Math.floor(Date.now() / 1000)
      return decodedPayload.exp < currentTime
    } catch (e) {
      console.error('Error checking token expiration:', e)
      return true
    }
  },

  isTokenExpired() {
    const token = this.getAccessToken()
    return this.isTokenExpiredFromToken(token)
  }
}

export default sessionService
